const STORAGE_PREFIX = "mozzoapp:waiter-operations:";
const SESSION_STORAGE_PREFIX = "mozzoapp:waiter-operations-session:";
const inMemoryWaiterOperationsStore = new Map();

export const WAITER_OPERATIONS_UPDATED_EVENT =
  "mozzoapp:waiter-operations-updated";
export const WAITER_OPERATIONS_REMOTE_SYNC_SOURCE = "remote-sync";
export const WAITER_SERVICE_REQUEST_ESCALATION_MS = 2 * 60 * 1000;

const normalizeTableReference = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("es")
    .replace(/\s+/g, " ");

export const isPendingWaiterServiceRequest = (request) =>
  Boolean(request?.id) && request?.status !== "completed";

export const isWaiterServiceRequestEscalated = (
  request,
  now = Date.now()
) => {
  if (!isPendingWaiterServiceRequest(request)) {
    return false;
  }

  const requestedAt = new Date(request?.requestedAt || 0).getTime();

  return (
    Number.isFinite(requestedAt) &&
    requestedAt > 0 &&
    Number(now || Date.now()) - requestedAt >= WAITER_SERVICE_REQUEST_ESCALATION_MS
  );
};

const doWaiterServiceRecordsShareTable = (leftRecord, rightRecord) => {
  if (!leftRecord || !rightRecord) {
    return false;
  }

  const leftBranchId = String(leftRecord?.branchId || leftRecord?.branch_id || "").trim();
  const rightBranchId = String(
    rightRecord?.branchId || rightRecord?.branch_id || ""
  ).trim();

  if (leftBranchId && rightBranchId && leftBranchId !== rightBranchId) {
    return false;
  }

  const leftTableId = String(leftRecord?.tableId || leftRecord?.id || "").trim();
  const rightTableId = String(rightRecord?.tableId || rightRecord?.id || "").trim();

  if (leftTableId && rightTableId && leftTableId === rightTableId) {
    return true;
  }

  const leftLabels = new Set(
    [
      leftRecord?.label,
      leftRecord?.mesa,
      Number(leftRecord?.tableNumber || 0) > 0
        ? `Mesa ${Number(leftRecord.tableNumber)}`
        : "",
    ]
      .map((value) => normalizeTableReference(value))
      .filter(Boolean)
  );
  const rightLabels = [
    rightRecord?.label,
    rightRecord?.mesa,
    Number(rightRecord?.tableNumber || 0) > 0
      ? `Mesa ${Number(rightRecord.tableNumber)}`
      : "",
  ]
    .map((value) => normalizeTableReference(value))
    .filter(Boolean);

  return rightLabels.some((label) => leftLabels.has(label));
};

export const doesWaiterServiceRequestMatchTable = (request, table) => {
  if (!isPendingWaiterServiceRequest(request) || !table) {
    return false;
  }

  return doWaiterServiceRecordsShareTable(request, table);
};

export const getVisibleWaiterServiceRequests = (
  serviceRequests,
  {
    branchId = "",
    waiterUserId = "",
    assignedTableIds = new Set(),
    now = Date.now(),
  } = {}
) =>
  (Array.isArray(serviceRequests) ? serviceRequests : []).filter((request) => {
    if (
      !isPendingWaiterServiceRequest(request) ||
      (branchId && request?.branchId && request.branchId !== branchId)
    ) {
      return false;
    }

    if (isWaiterServiceRequestEscalated(request, now)) {
      return true;
    }

    if (request?.assignedWaiterUserId) {
      return request.assignedWaiterUserId === waiterUserId;
    }

    if (request?.tableId && assignedTableIds?.has?.(request.tableId)) {
      return true;
    }

    // Sin un responsable concreto, el llamado queda abierto al equipo operativo.
    return true;
  });

export const buildWaiterOperationsStorageKey = (restaurantId) =>
  `${STORAGE_PREFIX}${restaurantId || "default"}`;

const buildWaiterOperationsSessionStorageKey = (restaurantId) =>
  `${SESSION_STORAGE_PREFIX}${restaurantId || "default"}`;

const toSafeText = (value, maxLength = 120) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const uniqueStrings = (values, maxItems = 12, maxLength = 120) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .filter(Boolean)
        .map((value) => toSafeText(value, maxLength))
        .filter(Boolean)
    )
  ).slice(0, maxItems);

const normalizeWaiterRosterEntry = (waiter) => ({
  userId: waiter?.userId || "",
  branchId: waiter?.branchId || "",
  branchAccessActive: waiter?.branchAccessActive !== false,
  companyUserActive: waiter?.companyUserActive !== false,
  profileFullName: String(waiter?.profileFullName || "").trim(),
  profilePhone: waiter?.profilePhone || "",
  createdAt: waiter?.createdAt || null,
  updatedAt: waiter?.updatedAt || new Date().toISOString(),
});

export const normalizeWaiterOperationsState = (payload) => ({
  tableAssignments: Array.isArray(payload?.tableAssignments)
    ? payload.tableAssignments
    : [],
  deliveryAlerts: Array.isArray(payload?.deliveryAlerts)
    ? payload.deliveryAlerts
    : [],
  serviceRequests: Array.isArray(payload?.serviceRequests)
    ? payload.serviceRequests
    : [],
  activeWaiters: Array.isArray(payload?.activeWaiters)
    ? payload.activeWaiters.map(normalizeWaiterRosterEntry).filter((waiter) => waiter.userId)
    : [],
});

const compactTableAssignment = (assignment) => ({
  tableId: assignment?.tableId || "",
  tableLabel: toSafeText(assignment?.tableLabel, 120),
  branchId: assignment?.branchId || "",
  waiterUserId: assignment?.waiterUserId || "",
  waiterName: toSafeText(assignment?.waiterName, 80),
  assignedAt: assignment?.assignedAt || "",
});

const compactDeliveryAlert = (alert) => ({
  id: alert?.id || "",
  orderId: alert?.orderId || alert?.requestId || "",
  branchId: alert?.branchId || "",
  tableId: alert?.tableId || "",
  mesa: toSafeText(alert?.mesa, 120),
  type: toSafeText(alert?.type || "delivery", 40),
  summary: toSafeText(alert?.summary, 180),
  itemIds: uniqueStrings(alert?.itemIds, 40, 160),
  source: toSafeText(alert?.source, 40),
  requestedAt: alert?.requestedAt || alert?.createdAt || "",
  broadcast: alert?.broadcast === true,
  targetWaiterIds: uniqueStrings(
    Array.isArray(alert?.targetWaiterIds)
      ? alert.targetWaiterIds
      : [alert?.waiterUserId],
    12,
    80
  ),
  targetWaiterNames: uniqueStrings(
    Array.isArray(alert?.targetWaiterNames)
      ? alert.targetWaiterNames
      : [alert?.waiterName],
    12,
    80
  ),
  seenByUserIds: uniqueStrings(alert?.seenByUserIds, 24, 80),
  status: toSafeText(alert?.status || "pending", 40),
  completedAt: alert?.completedAt || "",
  completedBy: alert?.completedBy || "",
});

const compactServiceRequest = (request) => ({
  id: request?.id || "",
  branchId: request?.branchId || request?.branch_id || "",
  tableId: request?.tableId || "",
  mesa: toSafeText(request?.mesa, 120),
  customerName: toSafeText(request?.customerName, 80),
  customerSessionId: toSafeText(request?.customerSessionId, 120),
  source: toSafeText(request?.source || "qr", 40),
  requestedAt: request?.requestedAt || request?.createdAt || "",
  options: uniqueStrings(request?.options, 8, 80),
  customText: toSafeText(request?.customText, 180),
  summary: toSafeText(request?.summary, 180),
  status: toSafeText(request?.status || "pending", 40),
  assignedWaiterUserId:
    request?.assignedWaiterUserId || request?.waiterUserId || "",
  assignedWaiterName: toSafeText(
    request?.assignedWaiterName || request?.waiterName,
    80
  ),
  assignedAt:
    request?.assignedAt ||
    (request?.waiterUserId ? request?.createdAt || request?.requestedAt || "" : ""),
  completedAt: request?.completedAt || "",
});

const WAITER_CALL_ALERT_TYPES = new Set([
  "service",
  "service_request",
  "call_waiter",
  "waiter_call",
  "need_help",
]);

export const getPendingWaiterServiceRequests = (waiterOperations, branchId = "") => {
  const normalizedState = normalizeWaiterOperationsState(waiterOperations);
  const pendingById = new Map();

  normalizedState.serviceRequests
    .map((request) => compactServiceRequest(request))
    .filter(
      (request) =>
        isPendingWaiterServiceRequest(request) &&
        (!branchId || !request.branchId || request.branchId === branchId)
    )
    .forEach((request) => pendingById.set(request.id, request));

  normalizedState.deliveryAlerts
    .map((alert) => compactDeliveryAlert(alert))
    .filter(
      (alert) =>
        alert.status === "pending" &&
        WAITER_CALL_ALERT_TYPES.has(String(alert.type || "").toLowerCase()) &&
        (!branchId || !alert.branchId || alert.branchId === branchId)
    )
    .forEach((alert) => {
      const requestId = alert.orderId || alert.id;

      if (!requestId || pendingById.has(requestId)) {
        return;
      }

      pendingById.set(requestId, {
        id: requestId,
        alertId: alert.id,
        branchId: alert.branchId || branchId,
        tableId: alert.tableId,
        mesa: alert.mesa,
        source: alert.source || "qr",
        requestedAt: alert.requestedAt,
        summary: alert.summary || "Cliente llama al mozo.",
        status: "pending",
        assignedWaiterUserId: alert.targetWaiterIds[0] || "",
        assignedWaiterName: alert.targetWaiterNames[0] || "",
        assignedAt: alert.requestedAt,
      });
    });

  const pendingByTable = new Map();
  const getRequestedAtTime = (request) => {
    const timestamp = new Date(request?.requestedAt || 0).getTime();
    return Number.isFinite(timestamp) && timestamp > 0
      ? timestamp
      : Number.MAX_SAFE_INTEGER;
  };

  Array.from(pendingById.values())
    .sort((left, right) => getRequestedAtTime(left) - getRequestedAtTime(right))
    .forEach((request) => {
      const requestBranchId = String(request?.branchId || branchId || "").trim();
      const tableId = String(request?.tableId || "").trim();
      const tableLabel = normalizeTableReference(request?.mesa);
      const tableKey = tableId
        ? `${requestBranchId}:id:${tableId}`
        : tableLabel
          ? `${requestBranchId}:label:${tableLabel}`
          : `${requestBranchId}:request:${request.id}`;

      if (!pendingByTable.has(tableKey)) {
        pendingByTable.set(tableKey, request);
      }
    });

  return Array.from(pendingByTable.values());
};

const compactWaiterOperationsState = (payload) => {
  const normalizedPayload = normalizeWaiterOperationsState(payload);
  const pendingRequests = normalizedPayload.serviceRequests.filter(
    (request) => request?.status !== "completed"
  );
  const completedRequests = normalizedPayload.serviceRequests.filter(
    (request) => request?.status === "completed"
  );
  const unseenAlerts = normalizedPayload.deliveryAlerts.filter(
    (alert) => !Array.isArray(alert?.seenByUserIds) || alert.seenByUserIds.length === 0
  );
  const seenAlerts = normalizedPayload.deliveryAlerts.filter(
    (alert) => Array.isArray(alert?.seenByUserIds) && alert.seenByUserIds.length > 0
  );

  return {
    tableAssignments: normalizedPayload.tableAssignments
      .slice(0, 120)
      .map((assignment) => compactTableAssignment(assignment)),
    deliveryAlerts: [...unseenAlerts, ...seenAlerts]
      .slice(0, 180)
      .map((alert) => compactDeliveryAlert(alert)),
    serviceRequests: [...pendingRequests, ...completedRequests]
      .slice(0, 220)
      .map((request) => compactServiceRequest(request)),
    activeWaiters: normalizedPayload.activeWaiters
      .slice(0, 120)
      .map((waiter) => normalizeWaiterRosterEntry(waiter)),
  };
};

const buildEmergencyWaiterOperationsState = (payload) => {
  const normalizedPayload = compactWaiterOperationsState(payload);
  const pendingRequests = normalizedPayload.serviceRequests.filter(
    (request) => request.status !== "completed"
  );
  const completedRequests = normalizedPayload.serviceRequests.filter(
    (request) => request.status === "completed"
  );
  const pendingAlerts = normalizedPayload.deliveryAlerts.filter(
    (alert) => alert.status === "pending"
  );

  return {
    tableAssignments: normalizedPayload.tableAssignments.slice(0, 60),
    deliveryAlerts: pendingAlerts.slice(0, 80),
    serviceRequests: [...pendingRequests, ...completedRequests].slice(0, 100),
    activeWaiters: normalizedPayload.activeWaiters.slice(0, 60),
  };
};

const getPendingServiceRequestIds = (payload) =>
  (Array.isArray(payload?.serviceRequests) ? payload.serviceRequests : [])
    .filter((request) => request?.status !== "completed")
    .map((request) => request.id)
    .filter(Boolean);

const emitWaiterOperationsUpdate = (
  restaurant,
  payload,
  newServiceRequestIds = [],
  source = "local"
) => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(WAITER_OPERATIONS_UPDATED_EVENT, {
      detail: {
        restaurantId: restaurant?.id || "default",
        payload,
        newServiceRequestIds,
        source,
      },
    })
  );
};

export const getWaiterOperationsState = (restaurant) => {
  if (typeof window === "undefined") {
    return normalizeWaiterOperationsState(null);
  }

  const storageKey = buildWaiterOperationsStorageKey(restaurant?.id);
  const sessionStorageKey = buildWaiterOperationsSessionStorageKey(restaurant?.id);

  if (inMemoryWaiterOperationsStore.has(storageKey)) {
    return normalizeWaiterOperationsState(
      inMemoryWaiterOperationsStore.get(storageKey)
    );
  }

  try {
    const rawValue =
      window.sessionStorage.getItem(sessionStorageKey) ||
      window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return normalizeWaiterOperationsState(null);
    }

    const parsedValue = normalizeWaiterOperationsState(JSON.parse(rawValue));
    inMemoryWaiterOperationsStore.set(storageKey, parsedValue);
    return parsedValue;
  } catch (error) {
    console.error("No pudimos leer la operacion de mozos:", error);
    return normalizeWaiterOperationsState(
      inMemoryWaiterOperationsStore.get(storageKey) || null
    );
  }
};

export const saveWaiterOperationsState = (restaurant, payload, options = {}) => {
  const storageKey = buildWaiterOperationsStorageKey(restaurant?.id);
  const sessionStorageKey = buildWaiterOperationsSessionStorageKey(restaurant?.id);
  const previousState = getWaiterOperationsState(restaurant);
  const normalizedPayload = compactWaiterOperationsState(payload);
  const serializedPreviousState = JSON.stringify(previousState);
  const serializedNextState = JSON.stringify(normalizedPayload);
  const previousServiceRequestIds = new Set(
    getPendingServiceRequestIds(previousState)
  );
  const nextServiceRequestIds = getPendingServiceRequestIds(normalizedPayload);
  const newServiceRequestIds = nextServiceRequestIds.filter(
    (id) => !previousServiceRequestIds.has(id)
  );

  if (typeof window === "undefined") {
    return normalizedPayload;
  }

  if (serializedPreviousState === serializedNextState) {
    return normalizedPayload;
  }

  try {
    window.localStorage.setItem(storageKey, serializedNextState);
    inMemoryWaiterOperationsStore.set(storageKey, normalizedPayload);
    try {
      window.sessionStorage.setItem(sessionStorageKey, serializedNextState);
    } catch (sessionMirrorError) {
      console.warn(
        "No pudimos espejar la operacion de mozos en sessionStorage:",
        sessionMirrorError
      );
    }
    emitWaiterOperationsUpdate(
      restaurant,
      normalizedPayload,
      newServiceRequestIds,
      options?.eventSource || "local"
    );
  } catch (error) {
    try {
      const emergencyPayload = buildEmergencyWaiterOperationsState(normalizedPayload);
      const serializedEmergencyPayload = JSON.stringify(emergencyPayload);
      try {
        window.sessionStorage.setItem(
          sessionStorageKey,
          serializedEmergencyPayload
        );
      } catch (sessionError) {
        console.warn(
          "No pudimos guardar la operacion de mozos en sessionStorage:",
          sessionError
        );
      }
      inMemoryWaiterOperationsStore.set(storageKey, emergencyPayload);
      emitWaiterOperationsUpdate(
        restaurant,
        emergencyPayload,
        newServiceRequestIds,
        options?.eventSource || "local"
      );
      return emergencyPayload;
    } catch (emergencyError) {
      console.error("No pudimos guardar la operacion de mozos:", emergencyError);
      inMemoryWaiterOperationsStore.set(storageKey, normalizedPayload);
      emitWaiterOperationsUpdate(
        restaurant,
        normalizedPayload,
        newServiceRequestIds,
        options?.eventSource || "local"
      );
      return normalizedPayload;
    }
  }

  return normalizedPayload;
};

export const assignWaiterToTable = (restaurant, assignment) => {
  const currentState = getWaiterOperationsState(restaurant);
  const nextAssignments = [
    {
      tableId: assignment.tableId,
      tableLabel: assignment.tableLabel || "",
      branchId: assignment.branchId || "",
      waiterUserId: assignment.waiterUserId || "",
      waiterName: assignment.waiterName || "",
      assignedAt: assignment.assignedAt || new Date().toISOString(),
    },
    ...currentState.tableAssignments.filter(
      (item) => item.tableId !== assignment.tableId
    ),
  ];

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    tableAssignments: nextAssignments,
  });
};

export const releaseWaiterTableAssignment = (
  restaurant,
  tableId,
  waiterUserId = ""
) => {
  if (!tableId) {
    return getWaiterOperationsState(restaurant);
  }

  const currentState = getWaiterOperationsState(restaurant);

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    tableAssignments: (currentState.tableAssignments || []).filter((assignment) => {
      if (assignment.tableId !== tableId) {
        return true;
      }

      if (waiterUserId && assignment.waiterUserId && assignment.waiterUserId !== waiterUserId) {
        return true;
      }

      return false;
    }),
  });
};

export const getWaiterAssignmentForTable = (restaurant, tableId) => {
  const currentState = getWaiterOperationsState(restaurant);
  return (
    currentState.tableAssignments.find((assignment) => assignment.tableId === tableId) ||
    null
  );
};

export const syncBranchActiveWaiters = (restaurant, branchId, waiters) => {
  if (!branchId) {
    return getWaiterOperationsState(restaurant);
  }

  const currentState = getWaiterOperationsState(restaurant);
  const normalizedWaiters = (Array.isArray(waiters) ? waiters : [])
    .map((waiter) =>
      normalizeWaiterRosterEntry({
        ...waiter,
        branchId: waiter?.branchId || branchId,
      })
    )
    .filter((waiter) => waiter.userId);

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    activeWaiters: [
      ...currentState.activeWaiters.filter((waiter) => waiter.branchId !== branchId),
      ...normalizedWaiters,
    ],
  });
};

export const getBranchActiveWaiters = (restaurant, branchId) => {
  if (!branchId) {
    return [];
  }

  const currentState = getWaiterOperationsState(restaurant);

  return (currentState.activeWaiters || []).filter(
    (waiter) =>
      waiter.branchId === branchId &&
      waiter.branchAccessActive === true &&
      waiter.companyUserActive !== false
  );
};

export const pickLeastBusyWaiterForServiceRequest = (
  restaurant,
  branchId,
  excludedRequestId = ""
) => {
  const currentState = getWaiterOperationsState(restaurant);
  const branchWaiters = getBranchActiveWaiters(restaurant, branchId);

  if (!branchWaiters.length) {
    return null;
  }

  const getPendingLoad = (waiterUserId) =>
    (currentState.serviceRequests || []).filter(
      (request) =>
        request.id !== excludedRequestId &&
        request.branchId === branchId &&
        request.status === "assigned" &&
        request.assignedWaiterUserId === waiterUserId
    ).length;

  const lowestLoad = Math.min(
    ...branchWaiters.map((waiter) => getPendingLoad(waiter.userId))
  );
  const tiedWaiters = branchWaiters.filter(
    (waiter) => getPendingLoad(waiter.userId) === lowestLoad
  );

  return tiedWaiters[Math.floor(Math.random() * tiedWaiters.length)] || null;
};

export const createDeliveryAlert = (restaurant, alert) => {
  const currentState = getWaiterOperationsState(restaurant);
  const nextAlert = {
    id: alert.id || `delivery-alert-${Date.now()}`,
    orderId: alert.orderId || "",
    branchId: alert.branchId || "",
    tableId: alert.tableId || "",
    mesa: alert.mesa || "",
    type: alert.type || "delivery",
    summary: alert.summary || "",
    itemIds: Array.isArray(alert.itemIds) ? alert.itemIds.filter(Boolean) : [],
    source: alert.source || "",
    requestedAt: alert.requestedAt || new Date().toISOString(),
    broadcast: Boolean(alert.broadcast),
    targetWaiterIds: Array.isArray(alert.targetWaiterIds)
      ? alert.targetWaiterIds.filter(Boolean)
      : [],
    targetWaiterNames: Array.isArray(alert.targetWaiterNames)
      ? alert.targetWaiterNames.filter(Boolean)
      : [],
    seenByUserIds: Array.isArray(alert.seenByUserIds) ? alert.seenByUserIds : [],
    status: alert.status || "pending",
  };
  const hasPendingCallForTable = currentState.deliveryAlerts.some(
    (existingAlert) =>
      existingAlert?.status === "pending" &&
      WAITER_CALL_ALERT_TYPES.has(String(existingAlert?.type || "").toLowerCase()) &&
      WAITER_CALL_ALERT_TYPES.has(String(nextAlert.type || "").toLowerCase()) &&
      doWaiterServiceRecordsShareTable(existingAlert, nextAlert)
  );

  if (hasPendingCallForTable) {
    return currentState;
  }

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    deliveryAlerts: [
      nextAlert,
      ...currentState.deliveryAlerts.filter(
        (existingAlert) => existingAlert.id !== nextAlert.id
      ),
    ],
  });
};

export const markDeliveryAlertSeen = (restaurant, alertId, waiterUserId) => {
  if (!alertId || !waiterUserId) {
    return getWaiterOperationsState(restaurant);
  }

  const currentState = getWaiterOperationsState(restaurant);

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    deliveryAlerts: currentState.deliveryAlerts.map((alert) =>
      alert.id !== alertId
        ? alert
        : {
            ...alert,
            seenByUserIds: Array.from(
              new Set([...(alert.seenByUserIds || []), waiterUserId])
            ),
          }
    ),
  });
};

export const completeDeliveryAlert = (restaurant, alertId, waiterUserId = "") => {
  if (!alertId) {
    return getWaiterOperationsState(restaurant);
  }

  const currentState = getWaiterOperationsState(restaurant);
  const completedAt = new Date().toISOString();

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    deliveryAlerts: currentState.deliveryAlerts.map((alert) =>
      alert.id !== alertId
        ? alert
        : {
            ...alert,
            status: "completed",
            completedAt,
            completedBy: waiterUserId || "",
            seenByUserIds: Array.from(
              new Set([...(alert.seenByUserIds || []), waiterUserId].filter(Boolean))
            ),
          }
    ),
  });
};

export const createWaiterServiceRequest = (restaurant, request) => {
  const currentState = getWaiterOperationsState(restaurant);
  const nextRequest = {
    id: request.id || `waiter-request-${Date.now()}`,
    branchId: request.branchId || "",
    tableId: request.tableId || "",
    mesa: request.mesa || "",
    customerName: String(request.customerName || "").trim().slice(0, 80),
    customerSessionId: request.customerSessionId || "",
    source: request.source || "qr",
    requestedAt: request.requestedAt || new Date().toISOString(),
    options: Array.isArray(request.options) ? request.options.filter(Boolean) : [],
    customText: request.customText || "",
    summary: request.summary || "",
    status: request.status || "pending",
    assignedWaiterUserId: request.assignedWaiterUserId || "",
    assignedWaiterName: request.assignedWaiterName || "",
    assignedAt: request.assignedAt || "",
  };
  const existingPendingRequest = currentState.serviceRequests.find(
    (existingRequest) =>
      isPendingWaiterServiceRequest(existingRequest) &&
      doWaiterServiceRecordsShareTable(existingRequest, nextRequest)
  );

  if (existingPendingRequest) {
    return currentState;
  }

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    serviceRequests: [
      nextRequest,
      ...currentState.serviceRequests.filter(
        (existingRequest) => existingRequest.id !== nextRequest.id
      ),
    ],
  });
};

export const assignWaiterServiceRequest = (restaurant, requestId, assignment) => {
  if (!requestId) {
    return getWaiterOperationsState(restaurant);
  }

  const currentState = getWaiterOperationsState(restaurant);

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    serviceRequests: currentState.serviceRequests.map((request) =>
      request.id !== requestId
        ? request
        : {
            ...request,
            status: assignment?.status || "assigned",
            assignedWaiterUserId: assignment?.assignedWaiterUserId || "",
            assignedWaiterName: assignment?.assignedWaiterName || "",
            assignedAt: assignment?.assignedAt || new Date().toISOString(),
          }
    ),
  });
};

export const completeWaiterServiceRequest = (restaurant, requestId) => {
  if (!requestId) {
    return getWaiterOperationsState(restaurant);
  }

  const currentState = getWaiterOperationsState(restaurant);
  const completedAt = new Date().toISOString();
  const targetRequest =
    currentState.serviceRequests.find((request) => request.id === requestId) ||
    currentState.deliveryAlerts.find((alert) => {
      const alertRequestId = alert.orderId || alert.requestId || "";
      return alert.id === requestId || alertRequestId === requestId;
    }) ||
    null;
  const completedRequestIds = new Set([requestId]);

  currentState.serviceRequests.forEach((request) => {
    if (
      isPendingWaiterServiceRequest(request) &&
      targetRequest &&
      doWaiterServiceRecordsShareTable(request, targetRequest)
    ) {
      completedRequestIds.add(request.id);
    }
  });

  return saveWaiterOperationsState(restaurant, {
    ...currentState,
    serviceRequests: currentState.serviceRequests.map((request) =>
      !completedRequestIds.has(request.id)
        ? request
        : {
            ...request,
            status: "completed",
            completedAt,
          }
    ),
    deliveryAlerts: currentState.deliveryAlerts.map((alert) => {
      const alertRequestId = alert.orderId || alert.requestId || "";
      const matchesRequest =
        completedRequestIds.has(alert.id) ||
        completedRequestIds.has(alertRequestId) ||
        Array.from(completedRequestIds).some(
          (completedRequestId) =>
            alert.id === `delivery-alert-${completedRequestId}` ||
            alert.id === `waiter-help-${completedRequestId}`
        ) ||
        (targetRequest &&
          alert.status === "pending" &&
          WAITER_CALL_ALERT_TYPES.has(String(alert.type || "").toLowerCase()) &&
          doWaiterServiceRecordsShareTable(alert, targetRequest));

      return matchesRequest
        ? {
            ...alert,
            status: "completed",
            completedAt,
          }
        : alert;
    }),
  });
};
